import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, AlertTriangle, ChevronDown, ChevronRight, ChevronsUpDown, Check, Loader2, Link, ArrowRight, Plus, Filter } from "lucide-react";
import { cn } from "@/lib/utils";
import { POLineItem } from "@/hooks/usePOLineItems";
import { useSkuMappings, useAvailableSkus, useCreateSkuMapping } from "@/hooks/useSkuMappings";
import { useCreateProduct } from "@/hooks/useProducts";
import { CreateProductDialog, CreateProductDialogResult } from "./CreateProductDialog";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useVariantIdentifierLinks } from "@/hooks/useVariantIdentifierLinks";
import {
  DEFAULT_PO_IDENTIFIER_TYPE,
  isPoLineMatched,
  type LegacyAliasRow,
} from "@/shared/variantMapping/poCodeMatching";
import { recalculateOpenPosForSkus } from "@/shared/variantMapping/recalculateOpenPoFunding";

interface POSkuMappingReviewProps {
  purchaseOrderId: string;
  organisationId: string;
  lineItems: POLineItem[];
  userEmail: string;
}

interface SkuGroup {
  sku: string;
  /** The type the PO reader gave this code: column header, or MPN (FIG-417). */
  identifierType: string;
  description: string | null;
  isMatched: boolean;
  breakdown?: POLineItem["extraction_source"] extends infer T
  ? T extends { sku_report_breakdown?: infer B }
  ? B
  : never
  : never;
  lineItemIds: string[];
}

/** FIG-417: the printed column header, or MPN when the reader found none. */
function identifierTypeOf(item: POLineItem): string {
  const source = item.extraction_source as { identifier_type?: unknown } | null | undefined;
  const type = typeof source?.identifier_type === 'string' ? source.identifier_type.trim() : '';
  return type || DEFAULT_PO_IDENTIFIER_TYPE;
}

const INITIAL_SKU_COUNT = 10;
const LOAD_MORE_COUNT = 20;

function SkuCombobox({
  availableSkus,
  value,
  onChange,
  placeholder = "Select SKU...",
  currentSku,
}: {
  availableSkus: string[];
  value: string | null;
  onChange: (sku: string | null) => void;
  placeholder?: string;
  currentSku?: string;
}) {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_SKU_COUNT);
  const [searchValue, setSearchValue] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const filteredSkus = useMemo(() => {
    let skus = currentSku
      ? availableSkus.filter((s) => s.toLowerCase() !== currentSku.toLowerCase())
      : availableSkus;
    if (searchValue) {
      const lower = searchValue.toLowerCase();
      skus = skus.filter((s) => s.toLowerCase().includes(lower));
    }
    return skus;
  }, [availableSkus, currentSku, searchValue]);

  // Reset visible count when search changes or dropdown opens
  useEffect(() => {
    setVisibleCount(INITIAL_SKU_COUNT);
  }, [searchValue]);

  useEffect(() => {
    if (open) {
      setVisibleCount(INITIAL_SKU_COUNT);
      setSearchValue("");
    }
  }, [open]);

  const visibleSkus = filteredSkus.slice(0, visibleCount);
  const hasMore = visibleCount < filteredSkus.length;

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20 && hasMore) {
      setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, filteredSkus.length));
    }
  }, [hasMore, filteredSkus.length]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between text-xs h-8"
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search Product..."
            className="h-8 text-xs"
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList ref={listRef} onScroll={handleScroll}>
            <CommandEmpty>No product found.</CommandEmpty>
            <CommandGroup>
              {value && (
                <CommandItem
                  onSelect={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  className="text-xs text-muted-foreground"
                >
                  Clear selection
                </CommandItem>
              )}
              {visibleSkus.map((sku) => (
                <CommandItem
                  key={sku}
                  value={sku}
                  onSelect={() => {
                    onChange(sku);
                    setOpen(false);
                  }}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      "mr-1 h-3 w-3",
                      value === sku ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {sku}
                </CommandItem>
              ))}
              {hasMore && (
                <div className="py-1 text-center text-xs text-muted-foreground">
                  Scroll for more ({filteredSkus.length - visibleCount} remaining)
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function POSkuMappingReview({
  purchaseOrderId,
  organisationId,
  lineItems,
  userEmail,
}: POSkuMappingReviewProps) {
  const [selectedSourceType, setSelectedSourceType] = useState<string>("all");

  // Fetch org connections + clone metadata to build source options
  const [orgConnections, setOrgConnections] = useState<Record<string, any> | null>(null);
  const [inventorySnapshotsEnabled, setInventorySnapshotsEnabled] = useState(false);
  const [productIdentifier, setProductIdentifier] = useState<string>("SKU");
  const [sourceOrganisationId, setSourceOrganisationId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchOrgData = async () => {
      const { data } = await (supabase as any)
        .from("organisations")
        .select("connections, inventory_snapshots_enabled, product_identifier, is_test_clone, source_organisation_id")
        .eq("id", organisationId)
        .single();
      if (data) {
        setOrgConnections((data.connections as Record<string, any>) || {});
        setInventorySnapshotsEnabled(!!data.inventory_snapshots_enabled);
        setProductIdentifier(data.product_identifier ?? "SKU");
        if (data.is_test_clone && data.source_organisation_id) {
          setSourceOrganisationId(data.source_organisation_id);
        }
      }
    };
    if (organisationId) fetchOrgData();
  }, [organisationId]);

  const { data: existingMappings } = useSkuMappings(organisationId, sourceOrganisationId);
  const { data: variantLinks = [] } = useVariantIdentifierLinks(organisationId);
  const { data: availableSkus = [] } = useAvailableSkus(organisationId, selectedSourceType === "all" ? undefined : selectedSourceType, sourceOrganisationId);
  const createMapping = useCreateSkuMapping();

  const derivedSourceOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [
      { value: "all", label: "All Sources" },
    ];
    if (!orgConnections) return options;

    const connectionTypes = new Set<string>();
    Object.values(orgConnections).forEach((conn: any) => {
      const type = conn.type?.toLowerCase() || "";
      if (type.includes("csv") && conn.import_type === "ecommerce") {
        connectionTypes.add("amazon_csv");
      } else if (type.includes("shopify")) {
        connectionTypes.add("shopify");
      } else if (type.includes("amazon")) {
        connectionTypes.add("amazon");
      }
    });

    if (connectionTypes.has("amazon")) options.push({ value: "amazon", label: "Amazon" });
    if (connectionTypes.has("shopify")) options.push({ value: "shopify", label: "Shopify" });
    if (connectionTypes.has("amazon_csv")) options.push({ value: "amazon_csv", label: "Amazon CSV Import" });
    if (inventorySnapshotsEnabled) options.push({ value: "snapshots", label: "Inventory Snapshots" });

    return options;
  }, [orgConnections, inventorySnapshotsEnabled]);
  const createProduct = useCreateProduct();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Local state for mapping changes: aliasSku -> selectedPrimarySku
  const [mappingChanges, setMappingChanges] = useState<Record<string, string | null>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [creatingProduct, setCreatingProduct] = useState<string | null>(null);
  const [createDialogData, setCreateDialogData] = useState<
    { sku: string; description: string | null; identifierType: string } | null
  >(null);

  // FIG-438 rule 6: a line is matched only when it is linked to a Variant.
  // This used to count any code that turned up anywhere in sales or inventory
  // data, case-insensitively, which is why this screen and Variant Mapping
  // disagreed about the very same code.
  const legacyAliases = useMemo<LegacyAliasRow[]>(
    () => (existingMappings ?? []).map((m) => ({ alias: m.alias_value, primary: m.primary_value })),
    [existingMappings],
  );

  // Group line items by unique SKU
  const skuGroups = useMemo(() => {
    const groups = new Map<string, SkuGroup>();
    lineItems.forEach((item) => {
      const sku = item.sku?.trim() || "";
      if (!sku) return; // Skip empty SKUs

      if (!groups.has(sku)) {
        const breakdown = item.extraction_source?.sku_report_breakdown;
        const identifierType = identifierTypeOf(item);
        groups.set(sku, {
          sku,
          identifierType,
          description: item.description,
          isMatched: isPoLineMatched({ value: sku, type: identifierType }, variantLinks, legacyAliases),
          breakdown: breakdown as SkuGroup["breakdown"],
          lineItemIds: [],
        });
      }
      groups.get(sku)!.lineItemIds.push(item.id);
    });
    return Array.from(groups.values());
  }, [lineItems, variantLinks, legacyAliases]);

  const matchedSkus = skuGroups.filter((g) => g.isMatched);
  const unmatchedSkus = skuGroups.filter((g) => !g.isMatched);
  const allMatched = unmatchedSkus.length === 0;

  const [isOpen, setIsOpen] = useState(!allMatched);

  // Reset state when PO changes
  useEffect(() => {
    setMappingChanges({});
    setIsOpen(!allMatched);
  }, [purchaseOrderId, allMatched]);

  // Build the list of SKUs available for the combobox
  // Include existing primary SKUs + matched SKUs from this PO
  const comboboxSkus = useMemo(() => {
    // Use a Map keyed by lowercase to deduplicate case-insensitively
    const skuMap = new Map<string, string>();
    availableSkus.forEach((s) => {
      const key = s.toLowerCase().trim();
      if (!skuMap.has(key)) skuMap.set(key, s);
    });
    matchedSkus.forEach((g) => {
      const key = g.sku.toLowerCase().trim();
      if (!skuMap.has(key)) skuMap.set(key, g.sku);
    });
    // Remove alias values so only primary values appear in the dropdown
    existingMappings?.forEach((m) => {
      skuMap.delete(m.alias_value.toLowerCase().trim());
    });
    return Array.from(skuMap.values()).sort();
  }, [availableSkus, matchedSkus, existingMappings]);

  // Build a lookup of existing alias -> primary mappings
  const existingMappingLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    existingMappings?.forEach((m) => {
      lookup.set(m.alias_value.toLowerCase().trim(), m.primary_value);
    });
    return lookup;
  }, [existingMappings]);

  // Build reverse lookup: primary -> list of aliases
  const primaryToAliasesLookup = useMemo(() => {
    const lookup = new Map<string, string[]>();
    existingMappings?.forEach((m) => {
      const key = m.primary_value.toLowerCase().trim();
      if (!lookup.has(key)) lookup.set(key, []);
      lookup.get(key)!.push(m.alias_value);
    });
    return lookup;
  }, [existingMappings]);

  const hasChanges = Object.values(mappingChanges).some((v) => v !== null);

  const handleSaveMappings = async () => {
    setIsSaving(true);
    try {
      // Build set of existing alias mappings to avoid duplicates
      const existingAliases = new Set(
        existingMappings?.map((m) => m.alias_value.toLowerCase().trim()) ?? []
      );

      // Every code the admin picked a product for. The legacy row is only
      // written for the ones that do not have one yet, but the Variant link has
      // to be attempted for all of them: a code with an existing legacy mapping
      // still needs the link, and a link that failed last time needs a retry.
      const selectedMappings = Object.entries(mappingChanges).filter(
        ([, primarySku]) => primarySku !== null,
      );
      // Insert new mappings
      const newMappings = selectedMappings.filter(
        ([aliasSku]) => !existingAliases.has(aliasSku.toLowerCase().trim())
      );

      const typeByCode = new Map(skuGroups.map((group) => [group.sku, group.identifierType]));
      let notLinked = 0;

      for (const [aliasSku, primarySku] of newMappings) {
        if (!primarySku) continue;
        await createMapping.mutateAsync({
          organisationId,
          primarySku,
          aliasSku,
          createdByEmail: userEmail,
          productIdentifier,
        });
      }

      // FIG-438 rule 4a: the legacy row alone leaves the code off the Variant,
      // so Variant Mapping never learns about it. Add it to the picked product's
      // Variant under this PO code's own type. The endpoint decides whether that
      // is allowed -- a picked value on no Variant, or on more than one, links
      // nothing and leaves the line unmatched.
      for (const [aliasSku, primarySku] of selectedMappings) {
        if (!primarySku) continue;
        try {
          const { data: linkResult } = await invokeFunction<{ linked?: boolean }>(
            supabase,
            "link-po-code-to-variant-v1",
            {
              body: {
                organisation_id: organisationId,
                po_code_value: aliasSku,
                po_code_type: typeByCode.get(aliasSku) ?? DEFAULT_PO_IDENTIFIER_TYPE,
                picked_identifier_value: primarySku,
                // Without these the decision RPC rebuilds the compatibility
                // mapping from the variant's oldest identifier and replaces the
                // alias -> primary row just saved with alias -> alias, losing
                // the SKU the admin picked.
                primary_type: productIdentifier,
                primary_value: primarySku,
              },
            },
          );
          if (linkResult?.linked !== true) notLinked += 1;
        } catch (linkError) {
          // The legacy mapping is already saved; losing the Variant link is
          // worth reporting but not worth discarding what did save.
          console.error("link-po-code-to-variant-v1 failed", aliasSku, linkError);
          notLinked += 1;
        }
      }

      // Trigger recalculation via edge function
      const { error: calcError } = await invokeFunction(supabase, 
        "calculate-po-funding",
        {
          body: { purchaseOrderId, organisationId },
        }
      );

      if (calcError) {
        console.error("Recalculation error:", calcError);
        toast({
          title: "Mappings saved",
          description: "Mappings were saved but recalculation encountered an error. Values may update shortly.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Mappings saved",
          description: `${newMappings.length} mapping(s) saved. Financial values have been recalculated.`,
        });
      }

      // A Variant link is organisation-wide, not this order's own. Any other
      // open PO carrying one of these codes is funded off the link that just
      // changed, so it is refreshed too -- the same follow-up grouping and a
      // mapping CSV import already do. Left unmatched (`matchPoCodes` off), so
      // refreshing another order cannot also re-decide its matches.
      void recalculateOpenPosForSkus(
        organisationId,
        selectedMappings.map(([aliasSku]) => aliasSku),
      );

      // Refresh line items and the links the matched badge reads.
      queryClient.invalidateQueries({ queryKey: ["po-line-items", purchaseOrderId] });
      queryClient.invalidateQueries({ queryKey: ["variant-identifier-links", organisationId] });
      if (notLinked > 0) {
        toast({
          title: "Some codes were not linked",
          description:
            `${notLinked} code(s) were saved as a mapping but not attached to a Variant, `
            + "so they stay unmatched. This happens when the chosen product is on no Variant, or on more than one.",
        });
      }
      setMappingChanges({});
    } catch (err) {
      console.error("Save mappings error:", err);
      toast({
        title: "Error saving mappings",
        description: "Failed to save SKU mappings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateProductWithType = async (result: CreateProductDialogResult) => {
    const sku = createDialogData?.sku;
    if (!sku) return;
    setCreatingProduct(sku);
    try {
      const typeMatchesOrg = result.identifierType === productIdentifier;
      const productSku = typeMatchesOrg
        ? sku
        : result.primaryValue ?? sku;

      // Create product — if it already exists, continue to mapping step
      let productAlreadyExisted = false;
      try {
        await createProduct.mutateAsync({
          organisationId,
          product: productSku,
          description: result.description,
          createdByEmail: userEmail,
          source: "po_mapping_review",
          identifierType: result.identifierType,
        });
      } catch (productErr: unknown) {
        const isDuplicate =
          productErr instanceof Error &&
          (productErr.message.includes("duplicate") || productErr.message.includes("already exists"));
        if (!typeMatchesOrg && isDuplicate) {
          productAlreadyExisted = true;
        } else {
          throw productErr;
        }
      }

      // Case B or C: identifier type differs from org primary — create a mapping
      if (!typeMatchesOrg) {
        await createMapping.mutateAsync({
          organisationId,
          primarySku: productSku,
          aliasSku: sku,
          createdByEmail: userEmail,
          productIdentifier,
          aliasType: result.identifierType,
        });
      }

      // FIG-438 rule 5: the legacy product and mapping alone leave nothing on
      // Variant Mapping. Create the Variant with this PO code as its identifier,
      // under the type the admin chose.
      let variantCreated = true;
      try {
        await invokeFunction(supabase, "create-variant-and-map-v1", {
          body: {
            organisation_id: organisationId,
            // The observed spelling, not the canonical one the Select showed:
            // the decision RPC looks the observation up by exact type and
            // raises when it finds none. See CreateProductDialogResult.
            identifier_type: result.variantIdentifierType,
            identifier_value: sku,
          },
        });
      } catch (variantError) {
        // The legacy rows are already written; say so rather than rolling back.
        variantCreated = false;
        console.error("create-variant-and-map-v1 failed", sku, variantError);
        toast({
          title: "Product created, variant not",
          description: "The product was saved but no variant was created, so this code stays unmatched.",
          variant: "destructive",
        });
      }

      // Trigger recalculation
      const { error: calcError } = await invokeFunction(supabase, 
        "calculate-po-funding",
        { body: { purchaseOrderId, organisationId } }
      );

      if (calcError) {
        console.error("Recalculation error:", calcError);
      }

      // Only when the Variant write succeeded. The destructive toast above
      // already said what happened, and following it with an unqualified
      // "Product created" left the success message as the last thing on screen,
      // reading as though the whole action had finished cleanly -- while the
      // code in fact stays unmatched.
      if (variantCreated) {
        toast({
          title: productAlreadyExisted ? "Mapping created" : "Product created",
          description: productAlreadyExisted
            ? `"${sku}" has been mapped to existing product "${productSku}".`
            : `"${productSku}" has been created as a new product.`,
        });
      }

      // The new Variant carries this code for the whole organisation, so any
      // other open PO holding it is funded off a link that did not exist a
      // moment ago. Same reason as the save path above.
      void recalculateOpenPosForSkus(organisationId, [sku]);

      setCreateDialogData(null);
      queryClient.invalidateQueries({ queryKey: ["po-line-items", purchaseOrderId] });
      // The matched badge reads the links, so a variant created here only shows
      // as matched once this is refetched.
      queryClient.invalidateQueries({ queryKey: ["variant-identifier-links", organisationId] });
    } catch (err) {
      console.error("Create product error:", err);
      toast({
        title: "Error creating product",
        description: "Failed to create product or mapping. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreatingProduct(null);
    }
  };

  if (skuGroups.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CardHeader className="py-3 px-4">
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full text-left">
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  Product Mapping Review
                </CardTitle>
                {allMatched ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    All {matchedSkus.length} products matched
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {matchedSkus.length} of {skuGroups.length} products matched
                  </Badge>
                )}
              </div>
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="px-4 pb-4 pt-0 space-y-4">
            {/* Source Filter */}
            {derivedSourceOptions.length > 1 && (
              <div className="flex items-center gap-2">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Product Source:</span>
                <Select value={selectedSourceType} onValueChange={setSelectedSourceType}>
                  <SelectTrigger className="w-[180px] h-8 text-xs">
                    <SelectValue placeholder="All Sources" />
                  </SelectTrigger>
                  <SelectContent>
                    {derivedSourceOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Unmatched SKUs */}
            {unmatchedSkus.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                  Unmatched Products ({unmatchedSkus.length})
                </h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left px-3 py-2 font-medium">Extracted Identifier</th>
                        <th className="text-left px-3 py-2 font-medium">Description</th>
                        <th className="text-left px-3 py-2 font-medium">Matched Product</th>
                        <th className="text-left px-3 py-2 font-medium">Or</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatchedSkus.map((group) => (
                        <tr key={group.sku} className="border-b last:border-0 bg-amber-50/30">
                          <td className="px-3 py-2 font-mono">
                            <div className="flex items-center gap-1">
                              <span>{group.sku}</span>
                              {mappingChanges[group.sku] && (
                                <>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="text-xs text-muted-foreground">{mappingChanges[group.sku]}</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">
                            {group.description || "—"}
                          </td>
                          <td className="px-3 py-2">
                            <SkuCombobox
                              availableSkus={comboboxSkus}
                              value={mappingChanges[group.sku] ?? null}
                              onChange={(val) =>
                                setMappingChanges((prev) => ({
                                  ...prev,
                                  [group.sku]: val,
                                }))
                              }
                              placeholder="Select Product..."
                              currentSku={group.sku}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs h-8 whitespace-nowrap"
                              disabled={creatingProduct === group.sku || !!mappingChanges[group.sku] || orgConnections === null}
                              onClick={() => setCreateDialogData({ sku: group.sku, description: group.description, identifierType: group.identifierType })}
                            >
                              {creatingProduct === group.sku ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              ) : (
                                <Plus className="h-3 w-3 mr-1" />
                              )}
                              Create New
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Matched SKUs */}
            {matchedSkus.length > 0 && (
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                  Matched Products ({matchedSkus.length})
                </h4>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left px-3 py-2 font-medium">Product</th>
                        <th className="text-right px-3 py-2 font-medium">Inventory</th>
                        <th className="text-right px-3 py-2 font-medium">12mo Sales</th>
                        <th className="text-left px-3 py-2 font-medium">Remap</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matchedSkus.map((group) => (
                        <tr key={group.sku} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono">
                            <div className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-600 shrink-0" />
                              <span>{group.sku}</span>
                              {mappingChanges[group.sku] ? (
                                <>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="text-xs text-muted-foreground">{mappingChanges[group.sku]}</span>
                                </>
                              ) : existingMappingLookup.get(group.sku.toLowerCase().trim()) && existingMappingLookup.get(group.sku.toLowerCase().trim())!.toLowerCase().trim() !== group.sku.toLowerCase().trim() ? (
                                <>
                                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                                  <span className="text-xs text-muted-foreground">
                                    (alias of {existingMappingLookup.get(group.sku.toLowerCase().trim())})
                                  </span>
                                </>
                              ) : primaryToAliasesLookup.get(group.sku.toLowerCase().trim())?.filter(a => a.toLowerCase().trim() !== group.sku.toLowerCase().trim()).length ? (
                                <span className="text-xs text-muted-foreground">
                                  (aliases: {primaryToAliasesLookup.get(group.sku.toLowerCase().trim())!.filter(a => a.toLowerCase().trim() !== group.sku.toLowerCase().trim()).join(", ")})
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {group.breakdown?.stock_held?.toLocaleString() ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {group.breakdown?.units_sold?.toLocaleString() ?? "—"}
                          </td>
                          <td className="px-3 py-2">
                            <SkuCombobox
                              availableSkus={comboboxSkus}
                              value={mappingChanges[group.sku] ?? null}
                              onChange={(val) =>
                                setMappingChanges((prev) => ({
                                  ...prev,
                                  [group.sku]: val,
                                }))
                              }
                              placeholder="Change..."
                              currentSku={group.sku}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Save Button */}
            {hasChanges && (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleSaveMappings}
                  disabled={isSaving}
                >
                  {isSaving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Save Mappings & Recalculate
                </Button>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>

      <CreateProductDialog
        open={createDialogData !== null}
        onOpenChange={(open) => {
          if (!open) setCreateDialogData(null);
        }}
        extractedIdentifier={createDialogData?.sku ?? ""}
        extractedDescription={createDialogData?.description ?? null}
        orgProductIdentifier={productIdentifier}
        poCodeType={createDialogData?.identifierType}
        onConfirm={handleCreateProductWithType}
        isCreating={creatingProduct !== null}
      />
    </Collapsible>
  );
}
